// Pure normalisation for the Google watchlist body (contract 27 §9 `service/creative/`).
// Framework-free: no Vue, no store, no `shared/http`.
import type {
  WatchlistCreative,
  WatchlistPage,
  WatchlistPagination,
} from '../../models/creativeWatchlist.types'

/**
 * The endpoint emits bare `NaN` tokens, which are not JSON — `JSON.parse` throws on them
 * (BE-27-02, v3 `GoogleCreativesView.vue:427, 484`). The pattern is ANCHORED to the `key:` side of
 * a pair on purpose:
 *
 *   `replaceAll('NaN', '0')` would rewrite any string VALUE containing "NaN" — an advertiser
 *   literally called "NaNo Labs" becomes "0o Labs" — and it lies about the datum (a missing
 *   number is not zero). `/:\s*NaN\b/g → ': null'` only touches a value position and preserves
 *   v3's replacement.
 *
 * Residual, and the reason BE-27-02 asks the backend to emit `null`: a string value that itself
 * contains `": NaN"` is still rewritten. No regex over a JSON *string* can fix that — only the
 * server can.
 */
export function sanitizeNaN(raw: string): string {
  return raw.replace(/:\s*NaN\b/gu, ': null')
}

/**
 * `transformResponse` entry point: sanitise, then parse. A body that is still not JSON after
 * sanitising is returned VERBATIM so the failure surfaces at decode time as a typed `ApiError`
 * rather than being swallowed into an empty page (v3 caught and `console.error`d it, `:444`).
 */
export function parseWatchlistBody(raw: unknown): unknown {
  if (typeof raw !== 'string') return raw
  try {
    return JSON.parse(sanitizeNaN(raw))
  } catch {
    return raw
  }
}

/** v3 `:399` folds `regions_shown` for display; v4 keeps it structured and formats at render. */
export function normalizeCreative(raw: WatchlistCreative): WatchlistCreative {
  const regions = raw.regions_shown
  return {
    ...raw,
    regions_shown: Array.isArray(regions)
      ? regions
      : typeof regions === 'string' && regions.length > 0
        ? regions.split(',').map((region) => region.trim())
        : [],
  }
}

/**
 * Folds the four total shapes the endpoint has been observed to send — `total_records` | `total` |
 * `count` | `next` — into one `WatchlistPagination` (v3 probes them inline at `:402–409`).
 */
export function normalizePagination(raw: unknown): WatchlistPagination {
  if (!isRecord(raw)) return {}
  const total = firstNumber(raw['total_records'], raw['total'], raw['count'])
  const next = typeof raw['next'] === 'string' ? raw['next'] : null
  const page = firstNumber(raw['page'])
  return {
    ...(total === undefined ? {} : { total }),
    ...(page === undefined ? {} : { page }),
    next,
  }
}

/**
 * Decode one page. THROWS on a body that is not a page — handed to `decodeDto` by the api class,
 * which turns it into a typed `ApiError` (03 §6). Never returns a silent empty page: "the backend
 * sent garbage" and "this workspace watches nobody" are different states (contract §14).
 */
export function decodeWatchlistPage(body: unknown): WatchlistPage {
  if (!isRecord(body) || !Array.isArray(body['data'])) {
    throw new Error('Malformed google-atc payload: expected { data: [], pagination: {} }')
  }
  return {
    data: (body['data'] as WatchlistCreative[]).map((item) => normalizeCreative(item)),
    pagination: normalizePagination(body['pagination']),
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function firstNumber(...candidates: unknown[]): number | undefined {
  for (const candidate of candidates) {
    if (typeof candidate === 'number' && Number.isFinite(candidate)) return candidate
  }
  return undefined
}
